/**
 * Analytics — Multi-provider Integration
 *
 * Supports Plausible, Google Tag Manager, Google Analytics 4, and Meta Pixel.
 * Each provider is optional and only loads when the corresponding env var is set.
 *
 *   VITE_PLAUSIBLE_ENABLED=true  +  VITE_PLAUSIBLE_DOMAIN=your-domain.com
 *   VITE_GTM_ID=GTM-XXXXXXX
 *   VITE_GA4_MARKETING_ID=G-XXXXXXXXXX   (public marketing/pricing/signup pages)
 *   VITE_GA4_APP_ID=G-XXXXXXXXXX         (authenticated app)
 *   VITE_META_PIXEL_ID=1234567890
 *
 * ── GA4 dual-property routing ──────────────────────────────────────
 * One gtag.js load, both measurement IDs configured, every hit routed
 * explicitly via `send_to` based on the current route:
 *
 *   - Public/marketing paths (see MARKETING_PATH_PREFIXES — mirrors the
 *     router's publicPaths list) → "EventFlow Marketing" property.
 *     Includes the whole signup funnel (/signup, /verify-email,
 *     /auth/callback) so ad-click → sign_up attribution stays in one
 *     property.
 *   - Everything else (routes behind auth) → "EventFlow App" property.
 *   - Attendee-facing transactional pages (/confirm/:id, /unsubscribe/:id)
 *     are excluded from GA4 entirely: visitors there are workshop
 *     attendees, not prospects or app users, and would pollute both
 *     properties.
 *
 * Automatic page_view is disabled (send_page_view: false); the router's
 * afterEach hook calls trackPageView() on every navigation instead, so
 * SPA route changes are counted exactly once in the right property.
 *
 * On dev hosts (localhost, dev.event-flow.co and its subdomains) all GA4
 * hits carry debug_mode (visible in GA4 DebugView) and
 * traffic_type: 'internal', which the GA4 data filters on both properties
 * match to keep dev traffic out of reportable data. GA4 data filters can
 * only act on traffic_type/debug — they cannot filter by hostname — so
 * this parameter is what actually separates dev from prod.
 * See notes/analytics-ga4-setup.md for the full property split and the
 * dev-traffic policy.
 *
 * Usage:
 *   import { initAnalytics, trackEvent, trackPageView } from '@/lib/analytics';
 *   initAnalytics();  // call once at startup
 *   trackEvent('campaign_created', { campaign_type: 'email' });
 */

const PLAUSIBLE_ENABLED = import.meta.env.VITE_PLAUSIBLE_ENABLED === 'true';
const PLAUSIBLE_DOMAIN = import.meta.env.VITE_PLAUSIBLE_DOMAIN || '';
const GTM_ID = import.meta.env.VITE_GTM_ID || '';
const GA4_MARKETING_ID = import.meta.env.VITE_GA4_MARKETING_ID || '';
const GA4_APP_ID = import.meta.env.VITE_GA4_APP_ID || '';
const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID || '';

const GA4_CONFIGURED = !!(GA4_MARKETING_ID || GA4_APP_ID);

/* ------------------------------------------------------------------ */
/*  GA4 property routing                                               */
/* ------------------------------------------------------------------ */

// Mirrors the router's publicPaths list (src/router/index.js) minus the
// attendee-facing transactional pages, which are excluded from GA4.
const MARKETING_PATH_PREFIXES = [
  '/login', '/logout', '/signup', '/forgot-password',
  '/reset-password', '/auth/callback', '/verify-email',
  '/docs', '/privacy', '/terms', '/style-guide',
  '/workspace-not-found',
];

// Workshop attendees confirming/unsubscribing — not prospects, not app users.
const GA4_EXCLUDED_PATH_PREFIXES = ['/confirm/', '/unsubscribe/'];

// Hosts whose GA4 hits are flagged with debug_mode (visible in DebugView).
function isDebugHost() {
  const host = window.location.hostname;
  return host === 'localhost'
    || host === '127.0.0.1'
    || host === 'dev.event-flow.co'
    || host.endsWith('.dev.event-flow.co');
}

/**
 * Which GA4 measurement ID should receive hits for the given path.
 * Returns null when the path is excluded or the matching property
 * isn't configured.
 *
 * @param {string} [path] - Route path (defaults to current location)
 * @returns {string | null}
 */
export function ga4TargetForPath(path) {
  const p = path || window.location.pathname;
  if (GA4_EXCLUDED_PATH_PREFIXES.some((prefix) => p.startsWith(prefix))) {
    return null;
  }
  const isMarketing = MARKETING_PATH_PREFIXES.some(
    (prefix) => p === prefix || p.startsWith(prefix)
  );
  return (isMarketing ? GA4_MARKETING_ID : GA4_APP_ID) || null;
}

/* ------------------------------------------------------------------ */
/*  Initializers                                                       */
/* ------------------------------------------------------------------ */

function initPlausible() {
  if (!PLAUSIBLE_ENABLED || !PLAUSIBLE_DOMAIN) return false;

  const script = document.createElement('script');
  script.defer = true;
  script.dataset.domain = PLAUSIBLE_DOMAIN;
  script.src = 'https://plausible.io/js/script.js';
  document.head.appendChild(script);
  return true;
}

function initGTM() {
  if (!GTM_ID) return false;

  window.dataLayer = window.dataLayer || [];
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);

  return true;
}

function initGA4() {
  if (!GA4_CONFIGURED) return false;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MARKETING_ID || GA4_APP_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());

  // send_page_view: false — the router afterEach sends page_view per route
  // so each hit lands in the correct property. debug_mode must be OMITTED
  // (not set to false) outside dev, or GA4 still treats hits as debug.
  // traffic_type: 'internal' is matched by the "internal traffic" data
  // filter on both GA4 properties, excluding dev hits from reports.
  const config = { send_page_view: false };
  if (isDebugHost()) {
    config.debug_mode = true;
    config.traffic_type = 'internal';
  }

  if (GA4_MARKETING_ID) window.gtag('config', GA4_MARKETING_ID, config);
  if (GA4_APP_ID) window.gtag('config', GA4_APP_ID, config);

  return true;
}

function initMetaPixel() {
  if (!META_PIXEL_ID) return false;

  /* Meta Pixel base code (official snippet adapted for dynamic injection) */
  const f = window;
  const b = document;
  if (f.fbq) return true;

  const n = f.fbq = function () {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
  };
  f._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];

  const script = b.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  b.head.appendChild(script);

  window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');

  return true;
}

/* ------------------------------------------------------------------ */
/*  Public API                                                         */
/* ------------------------------------------------------------------ */

/**
 * Initialize all configured analytics providers.
 * Call once at app startup (e.g., in main.js).
 */
export function initAnalytics() {
  const active = [];

  if (initPlausible()) active.push('Plausible');
  if (initGTM()) active.push('GTM');
  if (initGA4()) {
    active.push(`GA4 (${[GA4_MARKETING_ID && 'marketing', GA4_APP_ID && 'app'].filter(Boolean).join(' + ')})`);
  }
  if (initMetaPixel()) active.push('Meta Pixel');

  if (active.length) {
    console.info('[Analytics] Initialized:', active.join(', '));
  } else {
    console.info('[Analytics] Disabled — no provider keys configured');
  }
}

/**
 * Track a custom event across all active providers.
 * GA4 hits are routed to the marketing or app property based on the
 * current route (see ga4TargetForPath).
 *
 * @param {string} eventName - Event name (e.g., 'sign_up', 'campaign_created')
 * @param {Record<string, string | number | boolean>} [props] - Custom properties
 */
export function trackEvent(eventName, props) {
  if (PLAUSIBLE_ENABLED && window.plausible) {
    window.plausible(eventName, props ? { props } : undefined);
  }

  if (GTM_ID && window.dataLayer) {
    window.dataLayer.push({ event: eventName, ...props });
  }

  if (GA4_CONFIGURED && window.gtag) {
    const target = ga4TargetForPath();
    if (target) {
      window.gtag('event', eventName, { ...props, send_to: target });
    }
  }

  if (META_PIXEL_ID && window.fbq) {
    window.fbq('trackCustom', eventName, props);
  }
}

/**
 * Track a page view across all active providers.
 * Called by the router's afterEach hook on every SPA navigation.
 *
 * @param {string} [path] - Route fullPath (defaults to current location)
 */
export function trackPageView(path) {
  if (PLAUSIBLE_ENABLED && window.plausible) {
    window.plausible('pageview', path ? { u: window.location.origin + path } : undefined);
  }

  if (GA4_CONFIGURED && window.gtag) {
    // Route on the pathname only (fullPath may carry a query string).
    const pathname = (path || window.location.pathname).split('?')[0];
    const target = ga4TargetForPath(pathname);
    if (target) {
      window.gtag('event', 'page_view', {
        page_location: window.location.origin + (path || window.location.pathname),
        page_path: pathname,
        send_to: target,
      });
    }
  }

  if (META_PIXEL_ID && window.fbq) {
    window.fbq('track', 'PageView');
  }
}

/**
 * Attach the signed-in Supabase user id to all subsequent GA4 hits (both
 * properties) so marketing-site sessions and in-app activity join to the
 * account. Must be an opaque id — GA4 forbids PII (email) as user_id.
 *
 * @param {string} userId - Supabase auth user id (uuid)
 */
export function setAnalyticsUser(userId) {
  if (GA4_CONFIGURED && window.gtag && userId) {
    window.gtag('set', { user_id: userId });
  }
}

export function clearAnalyticsUser() {
  if (GA4_CONFIGURED && window.gtag) {
    window.gtag('set', { user_id: null });
  }
}

/**
 * Predefined event helpers for consistent naming across the app.
 * Names follow GA4 recommended-event conventions (snake_case); the ones
 * marked "key event" are imported as conversions in Google Ads / LinkedIn.
 */
export const AnalyticsEvents = {
  // Auth
  login: () => trackEvent('login'),
  logout: () => trackEvent('logout'),

  // Marketing funnel (→ EventFlow Marketing property)
  viewPricing: () => trackEvent('view_pricing'),
  signUpStart: (props) => trackEvent('sign_up_start', props),          // key event
  signUp: (props) => trackEvent('sign_up', { method: 'email', ...props }), // key event
  generateLead: (props) => trackEvent('generate_lead', props),         // key event

  // Campaigns
  campaignCreated: (props) => trackEvent('campaign_created', props),
  campaignUpdated: (props) => trackEvent('campaign_updated', props),

  // Events
  eventCreated: (props) => trackEvent('event_created', props),
  eventUpdated: (props) => trackEvent('event_updated', props),

  // Attendees
  attendeeAdded: (props) => trackEvent('attendee_added', props),
  attendeeImported: (props) => trackEvent('attendee_imported', props),

  // Communication
  emailSent: (props) => trackEvent('email_sent', props),
  smsSent: (props) => trackEvent('sms_sent', props),

  // Reports
  reportGenerated: (props) => trackEvent('report_generated', props),

  // Features
  featureUsed: (feature) => trackEvent('feature_used', { feature }),
  evaluationOcrUsed: (props) => trackEvent('evaluation_ocr_used', props),

  // Billing — GA4 standard purchase event (value + currency + transaction_id)
  purchase: (props) => trackEvent('purchase', { currency: 'USD', ...props }),
};
